import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../App';

export default function AiAssistant({ isOpen, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Load chat history
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      api.get('/api/ai/chat/history').then(res => {
        const hist = (res.data.messages || []).map((m, i) => ({
          id: i,
          role: m.role,
          content: m.content,
          timestamp: m.created_at,
        }));
        if (hist.length > 0) setMessages(hist);
        setSessionId(res.data.session_id);
      }).catch(() => {});
    }
  }, [isOpen]);

  const sendMessage = useCallback(async () => {
    if (!input.trim() || loading) return;
    const userMsg = { id: Date.now(), role: 'user', content: input.trim(), timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await api.post('/api/ai/chat', { message: userMsg.content, session_id: sessionId });
      const assistantMsg = { id: Date.now() + 1, role: 'assistant', content: res.data.response, timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, assistantMsg]);
      if (res.data.session_id) setSessionId(res.data.session_id);
    } catch {
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', content: 'Sorry, I encountered an error. Please try again.', timestamp: new Date().toISOString() }]);
    } finally { setLoading(false); }
  }, [input, loading, sessionId]);

  const clearChat = async () => {
    try {
      await api.delete('/api/ai/chat/history', { params: { session_id: sessionId } });
      setMessages([]);
    } catch {}
  };

  if (!isOpen) return null;

  const QUICK_PROMPTS = [
    "How do I handle the 'solar is too expensive' objection?",
    "Analyze my pipeline - what should I focus on?",
    "What's the current ITC tax credit breakdown?",
    "Best follow-up sequence for a qualified lead?",
  ];

  return (
    <div className="fixed inset-0 z-[100] flex justify-end" data-testid="ai-assistant-panel">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-[480px] h-full bg-surface border-l border-border shadow-2xl flex flex-col animate-slide-in">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-gradient-to-r from-primary/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-emerald-600 flex items-center justify-center shadow-glow-green">
              <i className="fa-solid fa-brain text-white text-sm" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-text-primary text-sm">Samaritan Solar AI</h3>
              <p className="text-[10px] text-primary font-semibold flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse-soft" />Online
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={clearChat} className="p-2 rounded-lg text-text-muted hover:text-destructive hover:bg-destructive-light transition-all" title="Clear chat" data-testid="clear-chat-btn">
              <i className="fa-solid fa-trash-can text-xs" />
            </button>
            <button onClick={onClose} className="p-2 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-alt transition-all" data-testid="close-ai-btn">
              <i className="fa-solid fa-xmark text-sm" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-8">
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-primary/10 flex items-center justify-center">
                <i className="fa-solid fa-comments text-primary text-xl" />
              </div>
              <h4 className="font-heading text-base font-semibold text-text-primary mb-1">How can I help?</h4>
              <p className="text-xs text-text-muted mb-6 max-w-xs mx-auto">I'm your AI solar sales expert. Ask me anything about leads, proposals, objection handling, or market strategy.</p>
              <div className="space-y-2">
                {QUICK_PROMPTS.map((prompt, i) => (
                  <button key={i} onClick={() => { setInput(prompt); }} className="block w-full text-left px-4 py-2.5 rounded-xl border border-border text-xs text-text-secondary hover:border-primary/20 hover:bg-primary/5 hover:text-primary transition-all" data-testid={`quick-prompt-${i}`}>
                    <i className="fa-solid fa-arrow-right text-[9px] mr-2 text-text-muted" />{prompt}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map(msg => (
            <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] ${msg.role === 'user' ? 'bg-primary text-white rounded-2xl rounded-br-md' : 'bg-surface-alt border border-border text-text-primary rounded-2xl rounded-bl-md'} px-4 py-3`}>
                {msg.role === 'assistant' && (
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <i className="fa-solid fa-brain text-primary text-[10px]" />
                    <span className="text-[10px] font-bold text-primary">Samaritan Solar AI</span>
                  </div>
                )}
                <div className={`text-sm leading-relaxed whitespace-pre-wrap ${msg.role === 'user' ? '' : 'text-text-secondary'}`}>
                  {msg.content}
                </div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-surface-alt border border-border rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <i className="fa-solid fa-brain text-primary text-[10px]" />
                  <span className="text-[10px] font-bold text-primary">Samaritan Solar AI</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-border bg-surface">
          <div className="flex items-end gap-2">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder="Ask about leads, proposals, strategy..."
                rows={1}
                className="w-full bg-surface-alt border border-border focus:border-primary focus:ring-2 focus:ring-primary/10 rounded-xl px-4 py-3 pr-10 text-sm text-text-primary outline-none transition-all resize-none"
                style={{ minHeight: '44px', maxHeight: '120px' }}
                data-testid="ai-chat-input"
              />
            </div>
            <button onClick={sendMessage} disabled={!input.trim() || loading}
              className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary text-white hover:bg-primary/90 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              data-testid="ai-send-btn"
            >
              <i className="fa-solid fa-paper-plane text-sm" />
            </button>
          </div>
          <p className="text-[10px] text-text-muted mt-2 text-center">Samaritan Solar Proprietary AI Engine</p>
        </div>
      </div>
    </div>
  );
}
